import React, { useCallback, useEffect } from 'react';
import { RTVIEvent } from "@pipecat-ai/client-js";
import {
  usePipecatClient,
  usePipecatClientTransportState,
  useRTVIClientEvent,
} from "@pipecat-ai/client-react";

const StartVideoButton = ({ onClick, onConnectionError, onRetryConnection, botConfig = "olivio_sales" }) => {
  const client = usePipecatClient();
  const transportState = usePipecatClientTransportState();
  const isConnected = ["connected", "ready"].includes(transportState);

  // Listen for bot disconnect events and trigger client disconnect
  useRTVIClientEvent(
    RTVIEvent.BotDisconnected,
    useCallback(async () => {
      console.log('Bot disconnected, triggering client disconnect');
      if (client) {
        try {
          await client.disconnect();
        } catch (error) {
          console.error("Error disconnecting client after bot disconnect:", error);
        }
      }
    }, [client])
  );


   

  const handleClick = useCallback(async () => {
    console.log('=== Start Video Button Clicked ===');
    console.log('Client:', !!client);
    console.log('Transport state:', transportState);
    console.log('Is connected:', isConnected);

    if (!client) {
      console.error("Pipecat client is not initialized");
      return;
    }

    try {
      if (isConnected) {
        console.log('Disconnecting...');
        await client.disconnect();
      } else {
        // Ensure client is fully disconnected before starting new connection
        if (transportState !== 'disconnected') {
          console.log('Client not fully disconnected, waiting...');
          try {
            await client.disconnect();
            // Wait a bit for disconnect to complete
            await new Promise(resolve => setTimeout(resolve, 100));
          } catch (err) {
            console.log('Pre-disconnect error (this is okay):', err);
          }
        }

        console.log('Starting new call...');
        console.log('Bot config:', botConfig);
        // Trigger instant calling screen BEFORE network call
        if (onClick) {
          onClick();
        }

        console.log("Calling backend:", process.env.REACT_APP_BACKEND_URL);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

        if (!backendUrl) {
          console.error("REACT_APP_BACKEND_URL is not configured");
          alert("Backend URL is not configured. Please check your environment settings.");
          return;
        }

        try {
          await client.startBotAndConnect({
            endpoint: `${backendUrl}/start-bot`,
            requestData: {
              bot_config: botConfig,
            },
          });
        } catch (connectError) {
          console.error("Connection error details:", connectError);

          // Disconnect client to clean up state
          try {
            await client.disconnect();
            // Wait for disconnect to complete
            await new Promise(resolve => setTimeout(resolve, 100));
          } catch (disconnectErr) {
            console.log('Error disconnecting after failed connection (this is okay):', disconnectErr);
          }

          // Check if it's an HTTP error response
          if (connectError.response) {
            const status = connectError.response.status;
            console.log('HTTP error status:', status);

            // Handle server error status codes (5xx)
            if (status >= 500 && status <= 599) {
              const errorMessages = {
                500: 'Internal Server Error',
                501: 'Not Implemented',
                502: 'Bad Gateway',
                503: 'Service Unavailable',
                504: 'Gateway Timeout'
              };

              const errorMessage = errorMessages[status] || 'Server Error';
              console.error(`Server error (${status}): ${errorMessage}`);

              if (onConnectionError) {
                onConnectionError({
                  type: 'server_error',
                  status: status,
                  message: errorMessage
                });
              }
              return;
            }
          }

          // Check if error message contains status information or CORS errors (often 502)
          const errorString = connectError.toString().toLowerCase();
          const errorMessage = connectError.message?.toLowerCase() || '';

          if (errorString.includes('500') || errorString.includes('502') ||
              errorString.includes('503') || errorString.includes('504') ||
              errorString.includes('gateway') || errorString.includes('unavailable') ||
              errorString.includes('cors') || errorMessage.includes('cors') ||
              errorString.includes('networkerror')) {
            console.error('Server error detected from error message or CORS issue (likely 502)');
            if (onConnectionError) {
              onConnectionError({
                type: 'server_error',
                message: 'Server Unavailable'
              });
            }
            return;
          }

          // For other connection errors, let them propagate
          throw connectError;
        }
      }
    } catch (error) {
      console.error("Connection error:", error);
      // Network or other errors that aren't handled above
      if (onConnectionError) {
        onConnectionError({
          type: 'network_error',
          message: 'Connection Failed'
        });
      }
    }
  }, [client, transportState, isConnected, onClick, onConnectionError]);

  // Expose handleClick to parent via callback
  useEffect(() => {
    if (onRetryConnection) {
      onRetryConnection(handleClick);
    }
  }, [onRetryConnection, handleClick]);

  const isDisabled = !client || ["connecting", "disconnecting"].includes(transportState);

  return (
    <button
        className="start-video-button"
        onClick={handleClick}
        disabled={isDisabled}>

      {isConnected ? "Disconnect" : "Start Video Chat"}
    </button>
  );
};

export default StartVideoButton;